import React, {
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

import { AgGridOverlay } from "@src/components/overlays/ag_grid_overlay";
import { StaffingRequestDetails } from "@src/features/staffing_request_details/components/staffing_request_details";
import TabsStaffingRequests from "@src/features/staffing_requests/components/tabs_staffing_requests/tabs_staffing_requests";
import { useStaffingRequestStateChangeHandler } from "@src/features/staffing_requests/hooks/use_staffing_request_state_change_handler";
import { ActiveFilterBar } from "@src/features/table_filtering/components/active_filter_bar";
import FiltersBar, {
  DownloadConfig,
} from "@src/features/table_filtering/components/filters_bar/filters_bar";
import { handleColumnResize } from "@src/features/table_filtering/utils/base_state_handler_utils";
import { mapToTreeData } from "@src/features/table_filtering/utils/filter_utils";
import {
  setColumnConfigs,
  setColumns,
  setFilterResult,
  setQuickFilter,
} from "@src/services/requestOverviewSlice";
import { RootState, useAppSelector } from "@src/setupStore";
import { TableSettingsConfig, TeamLeadRequestOverviewItem } from "@src/types";
import { userUtils } from "@src/utils/login_utils";
import {
  ColDef,
  Column,
  GridApi,
  GridReadyEvent,
  IRowNode,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import _ from "lodash";
import { useDispatch } from "react-redux";

import { AgGridProvider } from "@src/context/use_ag_grid_context";
import { createGridOptions } from "../../utils/request_overview_grid_options";
import { mapToRowData } from "../../utils/request_overview_utils";
import { FilterStaffingRequests } from "../filter_staffing_requests";
import "./request_overview.less";

interface RequestOverviewProps {
  requests: TeamLeadRequestOverviewItem[];
  isLoading: boolean;
}

const RequestOverview: React.FC<RequestOverviewProps> = ({
  requests,
  isLoading,
}) => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const gridApi = useRef<GridApi | null>(null);
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);

  const filterCallBack = useCallback(() => {
    if (gridApi.current) {
      const rows: TeamLeadRequestOverviewItem[] = [];
      gridApi.current.forEachNodeAfterFilter((node: IRowNode) => {
        if (node.data) {
          rows.push(_.cloneDeep(node.data));
        }
      });
      dispatch(setFilterResult([...rows]));
    }
  }, [dispatch]);

  const { activeFilters, resetActiveFilters, setTableRendered } =
    useStaffingRequestStateChangeHandler({
      gridApi,
      columnDefs,
      filterCallBack,
    });

  const {
    requestOverviewTableState: {
      filterResult,
      quickFilter,
      columns,
      columnConfigs,
    },
    modalSettings,
  } = useAppSelector((state: RootState) => state.requestOverviewSlice);

  const { NODE_ENV } = import.meta.env;

  const employeeId: string =
    NODE_ENV === "development"
      ? `?employeeId=${userUtils.getEmployeeData().id}`
      : "";

  const downloadConfig: DownloadConfig = {
    path: `/reports/export-staffing-request-report${employeeId}`,
    downloadableData: requests,
    downloadableFilteredData: filterResult,
    method: "POST",
  };

  const rowData = useMemo<TeamLeadRequestOverviewItem[]>(() => {
    return mapToRowData(requests);
  }, [requests]);

  function onGridReady(event: GridReadyEvent): void {
    gridApi.current = event.api;

    const colDefs: ColDef[] = gridApi.current
      .getColumns()
      .map((col: Column) => col.getColDef());
    setColumnDefs(colDefs);

    gridApi.current.sizeColumnsToFit();
  }

  function getFilters(): ReactNode {
    return (
      <FilterStaffingRequests
        regions={[
          ...mapToTreeData(requests, "region", true),
          { title: "N.A.", key: "N.A." },
        ]}
        countries={mapToTreeData(requests, "country", true)}
        projectName={mapToTreeData(requests, "projectName", true)}
        requestedRole={mapToTreeData(requests, "requestedRoleName", true)}
        disabled={isLoading}
      />
    );
  }

  function getTabs(): ReactNode {
    return <TabsStaffingRequests disabled={isLoading} />;
  }

  function onQuickFilterChange(value: string) {
    return dispatch(setQuickFilter(value));
  }

  function getTableSettings(): TableSettingsConfig {
    return {
      columns: columnDefs,
      onChange: (columns: string[]) => {
        dispatch(setColumns(columns));
        localStorage.setItem("roleRequestOverviewColumns", columns.toString());
      },
      columnsToHide: [],
      selectedColumns: localStorage.getItem("roleRequestOverviewColumns")
        ? localStorage.getItem("roleRequestOverviewColumns").split(",")
        : columns,
      lockedColumns: columnDefs
        .filter((col: ColDef) => col.lockVisible)
        .map((col: ColDef) => col.headerName),
      defaultHiddenColumns: [
        "Request ID",
        "Country",
        "Region",
        "Project Name",
        "Project Phase",
        "Country",
        "Region",
        "Tag",
        "Technology",
        "Required Action",
      ],
      compactViewSettings: {
        defaultCompacted: true,
        compactedColumns: ["Project Information", "Region/Country"],
        expandedColumns: ["Project Name", "Project Phase", "Country", "Region"],
      },
    };
  }

  return (
    <div className="request-overview">
      <FiltersBar
        drawerTitle="Filter Requests"
        isLoading={isLoading}
        tabs={getTabs()}
        filters={getFilters()}
        quickFilterDefaultValue={quickFilter}
        onQuickFilterChange={onQuickFilterChange}
        downloadConfig={downloadConfig}
        tableSettings={getTableSettings()}
      />
      <ActiveFilterBar
        activeFilters={activeFilters}
        onRemove={(key: string, value: string) =>
          resetActiveFilters(key, value)
        }
        onRemoveAll={() => resetActiveFilters()}
      />
      <AgGridProvider gridRef={gridRef}>
        <div style={{ height: "100%" }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            gridOptions={createGridOptions()}
            onGridReady={onGridReady}
            noRowsOverlayComponent={AgGridOverlay}
            noRowsOverlayComponentParams={{
              text: "No requests found",
              customStyle: { marginTop: "48px" },
            }}
            onColumnVisible={(props) => props.api.sizeColumnsToFit()}
            onFirstDataRendered={setTableRendered}
            onColumnResized={handleColumnResize(
              dispatch,
              setColumnConfigs,
              columnConfigs
            )}
          />
        </div>
        {modalSettings.detailsModalVisible && <StaffingRequestDetails />}
      </AgGridProvider>
    </div>
  );
};

export default React.memo(RequestOverview);
